import { useEffect, useMemo } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getHotel, getProfile, type Hotel, type HotelProfile } from '../features/hotels/api.js';
import { recordRecentItem } from '../features/navigation/recent-items.js';
import {
  listPlans,
  plansByHotelKey,
  type MonthlyPlan,
  type MonthlyPlanStatus,
} from '../features/plans/api.js';
import { PLAN_STATUS_LABEL } from '../features/plans/plan-status.js';
import { BrandVoiceForm } from '../features/hotels/brand-voice-form.js';
import { Button } from '../components/ui/button.js';

const HU_MONTHS = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

const PLAN_STATUS_TONE: Record<MonthlyPlanStatus, { bg: string; fg: string }> = {
  draft: { bg: 'var(--app-divider)', fg: 'var(--fg-2)' },
  topics_pending: { bg: 'var(--st-content-review-bg)', fg: 'var(--st-content-review-fg)' },
  content_pending: { bg: 'var(--ai-bg)', fg: 'var(--ai-fg)' },
  active: { bg: 'var(--brand-50)', fg: 'var(--brand-700)' },
  archived: { bg: 'var(--app-divider)', fg: 'var(--fg-faint)' },
};

function HotelProfilePage() {
  const { hotelId } = Route.useParams();
  const qc = useQueryClient();

  const hotel = useQuery<Hotel>({ queryKey: ['hotel', hotelId], queryFn: () => getHotel(hotelId) });
  const profile = useQuery<HotelProfile>({
    queryKey: ['hotel-profile', hotelId],
    queryFn: () => getProfile(hotelId),
  });
  const plans = useQuery<MonthlyPlan[]>({
    queryKey: plansByHotelKey(hotelId),
    queryFn: () => listPlans(hotelId),
  });

  // Record this hotel as a recently-visited item for the sidebar MRU.
  useEffect(() => {
    if (!hotel.data) return;
    recordRecentItem({
      kind: 'hotel',
      id: hotel.data.id,
      label: hotel.data.name,
      to: `/app/hotels/${hotel.data.id}`,
    });
  }, [hotel.data]);

  // Active = not explicitly archived AND endDate >= today (the same
  // rule as the /app/plans list view's default visibility filter).
  const activePlans = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return (plans.data ?? [])
      .filter((p) => {
        if (p.status === 'archived') return false;
        const endIso = p.endDate ?? `${p.year}-${String(p.month).padStart(2, '0')}-31`;
        return endIso >= todayIso;
      })
      .sort((a, b) => {
        // Closest start-date first, then by month.
        const aKey = `${a.year}-${String(a.month).padStart(2, '0')}`;
        const bKey = `${b.year}-${String(b.month).padStart(2, '0')}`;
        return aKey.localeCompare(bKey);
      });
  }, [plans.data]);

  if (profile.isLoading || hotel.isLoading) return <p>Betöltés…</p>;
  if (!profile.data || !hotel.data) return <p className="text-red-700">Nem található.</p>;

  return (
    <div className="space-y-6">
      <ActivePlansSection hotelId={hotelId} plans={activePlans} loading={plans.isLoading} />
      <BrandVoiceForm
        hotelId={hotelId}
        initial={profile.data}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['hotel-profile', hotelId] })}
      />
    </div>
  );
}

function ActivePlansSection({
  hotelId,
  plans,
  loading,
}: {
  hotelId: string;
  plans: MonthlyPlan[];
  loading: boolean;
}) {
  return (
    <section className="rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-5">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
            Aktív tervek
          </div>
          <h2 className="mt-0.5 text-h2 font-semibold text-[var(--ink-700)]">Aktuális időszak</h2>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/app/hotels/$hotelId/archive"
            params={{ hotelId }}
            className="text-meta font-medium text-[var(--brand-700)] hover:underline"
          >
            Archív tervek →
          </Link>
          <Link to="/app/plans/new" search={{ hotelId }}>
            <Button size="sm">+ Új terv</Button>
          </Link>
        </div>
      </header>

      {loading ? (
        <p className="text-body text-[var(--fg-2)]">Betöltés…</p>
      ) : plans.length === 0 ? (
        <div className="rounded-md border border-dashed border-[var(--app-divider)] bg-[var(--app-bg)] py-8 text-center">
          <p className="text-body text-[var(--fg-2)]">Még nincs aktív terv.</p>
          <p className="mt-1 text-meta text-[var(--fg-faint)]">
            Új terv létrehozásához kattints a fenti gombra.
          </p>
        </div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {plans.map((plan) => {
            const tone = PLAN_STATUS_TONE[plan.status];
            const monthLabel = HU_MONTHS[plan.month - 1] ?? plan.month;
            return (
              <li key={plan.id}>
                <Link
                  to="/app/plans/$hotelId/$year/$month"
                  params={{
                    hotelId,
                    year: String(plan.year),
                    month: String(plan.month),
                  }}
                  className="block rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)] p-3 transition-colors hover:border-[var(--brand-400)] hover:bg-[var(--app-surface)]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-body font-semibold text-[var(--ink-700)]">
                        {plan.year}. {monthLabel}
                      </div>
                      {plan.startDate && plan.endDate && (
                        <div className="mt-0.5 font-mono text-meta tabular-nums text-[var(--fg-faint)]">
                          {plan.startDate} → {plan.endDate}
                        </div>
                      )}
                    </div>
                    <span
                      className="inline-flex items-center rounded-pill px-2 py-0.5 text-meta font-semibold uppercase tracking-wider"
                      style={{ background: tone.bg, color: tone.fg }}
                    >
                      {PLAN_STATUS_LABEL[plan.status]}
                    </span>
                  </div>
                  {plan.brief && (
                    <p className="mt-1.5 line-clamp-2 text-meta text-[var(--fg-2)]">{plan.brief}</p>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId/')({
  component: HotelProfilePage,
});
